import * as React from 'react';
import moment from 'moment';
import { useEffect, useState, useContext } from 'react';
import FileDownload from 'js-file-download';
import { Container } from '../helpers/Grid';
import CalendarProps from './type';
import { Loader, SvgImage } from '../common';
import FiltersPanel from '../FiltersPanel';
import CalendarMonth from '../CalendarMonth';
import JumpList from '../common/JumpList';
import CalendarItemsList from '../CalendarItemsList';
import mapFilterType from '../../mutators';
import LocalizationContext from '../../contexts/Localizations';
import breakpoints from '../../breakpoints';
import { getUrlParamValue } from '../../utilities/browser';
import {
  QueryStringObject,
  CalendarApiQueryParams,
  CalendarItem,
  CalendarApiResponse,
  CalendarDownloadApiParams,
  ApiFilter,
  CalendarMonth as CalendarMonths,
} from '../../core/typings/api';
import {
  mapQueryStringParams,
  generateQueryString,
} from '../../modules/Calendar/methods';
import api from '../../core/api/models';
import './styles.scss';

interface FilterOption {
  displayText: string;
  selectedValue: boolean;
  value: string;
  futureYear?: number;
}

const Calendar = ({
  filters,
  disciplineCode,
  isPast = false,
  bearerToken,
  allowMultipleSelection,
  isCwcResult,
  calendarDownloadOptions,
}: CalendarProps) => {
  const apiEndpoint = isPast ? api.calendar.getPast : api.calendar.getUpcoming;

  const [isFetchingData, setIsFetchingData] = useState<boolean>(false);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const [calendarMonths, setCalendarMonths] = useState<CalendarMonths[]>([]);
  const [endPointFilters, setEndpointFilters] = useState<ApiFilter[]>();
  const [selectedMonthIndex, setSelectedMonthIndex] = useState<number>(0);
  const [searchResults, setSearchResults] = useState<CalendarItem[]>();
  const [searchTerm, setSearchTerm] = useState<string>(
    getUrlParamValue('searchTerm') || '',
  );
  const [years, setYears] = useState<ApiFilter>();
  const [selectedYear, setSelectedYear] = useState<string>(
    getUrlParamValue('year') || '',
  );
  const [isMobile, setIsMobile] = useState<boolean>(window.outerWidth < breakpoints.lg);
  const [apiQueryParams, setApiQueryParams] = useState<CalendarApiQueryParams>(
    mapQueryStringParams(disciplineCode),
  );
  const [apiError, setApiError] = useState<string>();
  const [downloadOptions, setDownloadOptions] = useState<FilterOption[]>();
  const [downloadParams, setDownloadParams] = useState<CalendarDownloadApiParams>();
  const currentYearValue = new Date().getFullYear();
  const nextYearValue = currentYearValue + 1;
  const yearAfterNextValue = currentYearValue + 2;
  const { translations } = useContext(LocalizationContext);

  /**
   * Handles the download calendar
   */
  const handleDownload = (event: any, token: string) => {
    const url = event.value;
    const fileType = event.displayText;
    setIsDownloading(true);
    const isFutureYear = event.futureYear && downloadParams?.Query.year;
    const futureYearValue = event.futureYear;
    const futureYearParams = isFutureYear ? {
      ...downloadParams,
      Query: {
        ...downloadParams.Query,
        year: futureYearValue?.toString(),
        country: downloadParams?.Query?.country,
        discipline: downloadParams?.Query?.discipline,
        raceCategory: downloadParams?.Query?.category,
        raceType: downloadParams?.Query?.raceType,
        raceClass: downloadParams?.Query?.competitionClass,
        seasonId: downloadParams?.Query?.seasonId,
      },
      ReportTitle: `${disciplineCode || apiQueryParams.discipline} - ${futureYearValue}`,
    } : null;

    api.calendar.downloadCalendar(
      url,
      token,
      isFutureYear ? futureYearParams as CalendarDownloadApiParams
        : downloadParams as CalendarDownloadApiParams,
    ).then((response) => {
      FileDownload(
        response.data,
        `UCICompetitions_${downloadParams?.Query.discipline}_${
          (isFutureYear ? futureYearParams?.Query.year : downloadParams?.Query.year)}.${fileType}`,
      );
      setIsDownloading(false);
    });
  };

  /**
   * Fetch Calendar data from endpoint and set data
   * to state.
   */
  const fetchCalendarData = async () => {
    setIsFetchingData(true);

    try {
      const upcomingPastData: CalendarApiResponse = await apiEndpoint(
        apiQueryParams,
      );
      const cwcData: CalendarApiResponse = await api.calendar.getCwc(apiQueryParams.discipline || '');

      const data = isCwcResult ? cwcData : upcomingPastData;

      const yearFilter = data.filters?.find((filter) => (
        filter.queryParam === 'year'
      ));

      setIsFetchingData(false);

      if (data.items) {
        if (isCwcResult) {
          const augustItems = data.items.find((item) => item.year === 2023 && item.month === 8);
          const pastCwcRaces = augustItems?.items
            .filter((item) => moment(item.competitionDate).isBefore(moment()));
          const items = pastCwcRaces?.length ? [{ ...augustItems, items: pastCwcRaces }] : [];

          setCalendarMonths(items as CalendarMonths[]);
          setSelectedMonthIndex(0);
        } else if (isPast) {
          setCalendarMonths(data.items.reverse());
          setSelectedMonthIndex(data.items.length - 1);
        } else {
          setCalendarMonths(data.items);
          setSelectedMonthIndex(0);
        }
      }

      if (data.filters) {
        setEndpointFilters(isCwcResult ? undefined : data.filters);
      }

      if (yearFilter) {
        const selectedYearValue = yearFilter.items.find((year) => year.selected);
        setYears(yearFilter);
        if (selectedYearValue) {
          setSelectedYear(selectedYearValue.code.toString());
        }
      }

      if (data.downloadButtons) {
        const currentYear = new Date().getFullYear();
        const dataYear = Number(data.downloadButtons.year);
        const validatedYear = dataYear <= currentYear ? dataYear : currentYear;

        // Special case for CRO which runs in a bespoke way and must be updated manually.
        const checkedYear = apiQueryParams.discipline === 'CRO' ? '2025' : validatedYear;

        setDownloadParams(
          {
            IsGrouped: true,
            Language: 'En',
            Query: {
              discipline: apiQueryParams.discipline || disciplineCode,
              year: `${apiQueryParams.year || checkedYear}`,
              country: apiQueryParams.country,
              category: apiQueryParams.raceCategory,
              raceType: apiQueryParams.raceType,
              competitionClass: apiQueryParams.raceClass,
              seasonId: apiQueryParams.seasonId,
            },
            ReportTitle: `${disciplineCode || apiQueryParams.discipline} - ${String(apiQueryParams.year || checkedYear)}`,
          },
        );

        const isAll = window.location.pathname.startsWith('/calendar/all');

        const { discipline, ...restApiQueryParams } = apiQueryParams;

        const isMultipleSelected = Object.values(isAll ? apiQueryParams : restApiQueryParams)
          .some((param) => param);

        const defaultDownloadOptions = [
          {
            displayText: isMultipleSelected
              ? translations.calendar.downloadSelectionLabel
              : translations.calendar.downloadSeasonLabel,
            selectedValue: true,
            value: '',
          },
          {
            displayText: 'pdf',
            selectedValue: false,
            value: data.downloadButtons.downloadPdfUrl,
          },
          {
            displayText: 'xls',
            selectedValue: false,
            value: data.downloadButtons.downloadXlsUrl,
          },
        ];

        const nextYearOptions = [
          {
            displayText: 'pdf',
            selectedValue: false,
            value: data.downloadButtons.downloadPdfUrl,
            futureYear: nextYearValue,
          },
          {
            displayText: 'xls',
            selectedValue: false,
            value: data.downloadButtons.downloadXlsUrl,
            futureYear: nextYearValue,
          },
        ];

        const nextTwoYearOptions = [
          {
            displayText: 'pdf',
            selectedValue: false,
            value: data.downloadButtons.downloadPdfUrl,
            futureYear: nextYearValue,
          },
          {
            displayText: 'xls',
            selectedValue: false,
            value: data.downloadButtons.downloadXlsUrl,
            futureYear: nextYearValue,
          },
          {
            displayText: 'pdf',
            selectedValue: false,
            value: data.downloadButtons.downloadPdfUrl,
            futureYear: yearAfterNextValue,
          },
          {
            displayText: 'xls',
            selectedValue: false,
            value: data.downloadButtons.downloadXlsUrl,
            futureYear: yearAfterNextValue,
          },
        ];

        let combinedDownloadOptions = [...defaultDownloadOptions];

        if (calendarDownloadOptions === 'Current and Next') {
          combinedDownloadOptions = [
            ...combinedDownloadOptions,
            ...nextYearOptions,
          ];
        } else if (calendarDownloadOptions === 'Current and Next Two') {
          combinedDownloadOptions = [
            ...combinedDownloadOptions,
            ...nextTwoYearOptions,
          ];
        }

        setDownloadOptions(combinedDownloadOptions);
      }
    } catch (error) {
      setApiError(error as string);
    }
  };

  /**
   * Reset search term and results.
   */
  const resetSearch = () => {
    setSearchResults(undefined);
    setSearchTerm('');
  };

  /**
   * Runs though all months and data stored in state
   * and returns a list of events if the name or
   * venue string inclue the keyword.
   *
   * We also want this to persist so we append/remove
   * it from the querystring accordingly.
   * @param keyword
   */
  const handleSearch = (keyword: string) => {
    setSearchTerm(keyword);
    generateQueryString({
      searchTerm: keyword,
    });
    if (keyword.length < 1) {
      setSearchResults(undefined);
      return;
    }
    let results: CalendarItem[] = [];
    calendarMonths.forEach((month) => {
      month.items.forEach((day) => {
        results = [
          ...results,
          ...day.items.filter((event) => (
            event.name?.toLocaleLowerCase().includes(
              keyword.toLocaleLowerCase(),
            ) || event.venue?.toLocaleLowerCase().includes(
              keyword.toLocaleLowerCase(),
            )
          )),
        ];
      });
    });
    setSearchResults(results);
  };

  /**
   * Handles next calendar month
   */
  const handleNextMonth = () => {
    if (selectedMonthIndex < (calendarMonths.length - 1)) {
      setSelectedMonthIndex(selectedMonthIndex + 1);
    }
  };

  /**
   * Handles previous calendar month
   */
  const handlePreviousMonth = () => {
    if (selectedMonthIndex !== 0 && calendarMonths.length !== 0) {
      setSelectedMonthIndex(selectedMonthIndex - 1);
    }
  };

  /**
   * Updates the apiQueryParams
   * @param params
   */
  const handleFilterChange = (params: any) => {
    resetSearch();
    setTimeout(() => {
      const state = { ...apiQueryParams, ...params.selected };

      if (state.discipline === 'CRO') {
        state.year = '2025';
      }

      setApiQueryParams(state);
      setIsDownloading(false);
    }, 100);
  };

  /**
   * Set resize event listener to check if breakpoint
   * is below lg. We use the isMobile varible to if we
   * need to push the year filter through to the
   * FilterPanel (visibleFilters).
   */
  useEffect(() => {
    if (years) {
      window.addEventListener('resize', () => {
        setIsMobile(window.outerWidth < breakpoints.lg);
      });
    }
  }, [years]);

  /**
   * Fetch Calendar data whenever the apiQueryParams
   * updates.
   */
  useEffect(() => {
    if (apiQueryParams) {
      generateQueryString(
        apiQueryParams as QueryStringObject,
      );
    }
    fetchCalendarData();
  }, [apiQueryParams]);

  /**
   * We get the searchterm from the query string
   * when the component mounts. At this point the
   * calendar items have not yet been loaded so
   * we need to wait until we recieve calendarMonths
   * in state then trigger a handleSeach.
   */
  useEffect(() => {
    if (calendarMonths
        && searchTerm !== ''
        && !isFetchingData) {
      handleSearch(searchTerm || '');
    }
  }, [calendarMonths, isFetchingData]);

  /**
   * Updates the year of the download query object
   */
  useEffect(() => {
    if (selectedYear) {
      if (downloadParams?.Query?.discipline === 'CRO') {
        setSelectedYear(nextYearValue.toString());
      }

      setDownloadParams({
        ...downloadParams,
        ReportTitle: `${disciplineCode} - ${selectedYear}`,

        Query: {
          ...downloadParams?.Query,
          Year: Number(selectedYear),
          Country: downloadParams?.Query?.country,
          Discipline: downloadParams?.Query?.discipline,
          Category: downloadParams?.Query?.category,
          RaceType: downloadParams?.Query?.raceType,
          CompetitionClass: downloadParams?.Query?.competitionClass,
          SeasonId: downloadParams?.Query?.seasonId,
        },
      } as CalendarDownloadApiParams);
    }
  }, [selectedYear]);

  return (
    <section className={`calendar ${isCwcResult ? 'calendar--embedded' : ''}`}>
      <Container>
        <>
          <div className="calendar__toolbar">
            {filters && (
              <FiltersPanel
                hasSearch
                visibleFilters={isMobile && years ? [...filters, years] : filters}
                additionalFilters={endPointFilters}
                additionalFilterCallback={
                  (params: CalendarApiQueryParams) => handleFilterChange(params)
                }
                visibleFilterCallback={
                  (params: CalendarApiQueryParams) => handleFilterChange(params)
                }
                searchLabel={translations.calendar.searchText}
                searchPlaceholder={translations.calendar.searchLabel}
                additionalFiltersLabel={translations.calendar.moreFiltersLabel}
                additionalFiltersApplyButtonLabel={translations.calendar.applyFiltersLabel}
                searchFieldCallback={handleSearch}
                searchTerm={searchTerm || ''}
                allowMultipleSelection={allowMultipleSelection}
              />
            )}
            <div className="calendar__floating-filters">
              {(downloadOptions && apiQueryParams.discipline && !isCwcResult) && (
                <div className={`calendar__download-filter ${isDownloading && 'is-downloading'}`}>
                  <JumpList
                    items={downloadOptions}
                    selectedValue={downloadOptions[0].displayText}
                    theme="filter-style"
                    icon="icon-right-paddle"
                    action={(event: any) => {
                      handleDownload(event, bearerToken);
                    }}
                    disableFirst={
                      Boolean(!isFetchingData && !calendarMonths.length) || isDownloading
                    }
                  />

                  {isDownloading && <Loader />}
                </div>
              )}
              {(!apiQueryParams.discipline && !isCwcResult) && (
                <div className="calendar__download-disabled">
                  <JumpList
                    items={[
                      {
                        displayText: translations.calendar.downloadLabel,
                        selectedValue: true,
                        value: '',
                      },
                    ]}
                    selectedValue={translations.calendar.downloadLabel}
                    theme="filter-style"
                    icon="icon-right-paddle"
                    action={() => null}
                    disableFirst
                  />
                  <p>{translations.calendar.downloadHelpText}</p>
                </div>
              )}
              {years && (
                <div className="calendar__year-filter">
                  <JumpList
                    items={mapFilterType(years.items)}
                    selectedValue={selectedYear}
                    useSearch
                    theme="filter-style"
                    icon="icon-plus"
                    useScroller
                    action={(event: any) => {
                      setSelectedYear(event.value);
                      handleFilterChange({
                        selected: {
                          year: event.value,
                        },
                      });
                    }}
                  />
                </div>
              )}
            </div>
          </div>

          <div className="calendar__months-view">
            {isFetchingData && (
              <div className="calendar__loading-state">
                <Loader />
              </div>
            )}

            {Boolean(!isFetchingData && !calendarMonths.length) && (
              <div className="calendar__no-results">
                <h3>
                  {translations.calendar.noResults}
                </h3>
              </div>
            )}

            {Boolean(apiError) && (
              <div className="calendar__api-error">
                <h3>
                  {apiError}
                </h3>
              </div>
            )}

            {searchResults && !isFetchingData && (
              <div className="calendar__search-results">
                {Boolean(searchResults.length) && (
                  <CalendarItemsList items={searchResults} />
                )}
                {Boolean(searchResults.length) === false && (
                  <div className="calendar__no-results">
                    <h3>
                      {translations.calendar.noSearchResults}
                    </h3>
                  </div>
                )}
              </div>
            )}

            {Boolean(calendarMonths.length && !isFetchingData && !searchResults) && (
              <>
                <div className="calendar__month-navigation">
                  <button
                    className="calendar__month-navigation__previous-month"
                    onClick={() => handlePreviousMonth()}
                    disabled={selectedMonthIndex === 0}
                  >
                    <SvgImage title="previous month" reference="#icon-right-paddle" />
                  </button>
                  <button
                    className="calendar__month-navigation__next-month"
                    onClick={() => handleNextMonth()}
                    disabled={selectedMonthIndex === (calendarMonths.length - 1)}
                  >
                    <SvgImage title="next month" reference="#icon-right-paddle" />
                  </button>
                </div>
                <CalendarMonth {...calendarMonths[selectedMonthIndex]} />
              </>
            )}
          </div>
        </>
      </Container>
    </section>
  );
};

export default Calendar;
